// Video Export guide — the standalone "Video Export" kebab-menu flow that
// renders scenes into an MP4 (VideoExportTabV2), gated by SaveRequiredGate
// until the training has a saved version on the server. This is a separate,
// deeper flow from the regular Save/Preview/Export guide — see that guide for
// SCORM/URL export.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const videoExportGuide = {
  id: "video-export",
  icon: "movie_creation",
  keywords: [
    "video export",
    "video dışa aktarma",
    "video dışa aktar",
    "mp4",
    "render",
    "record",
    "kayıt",
    "export video",
    "video olarak dışa aktar",
    "download video",
    "video indir",
    "merge",
    "birleştir",
    "regenerate",
    "yeniden üret",
    "subtitle",
    "altyazı",
    "dubbing",
    "seslendirme",
    "translate video",
    "video çevirisi",
    "language",
    "dil",
    "history",
    "geçmiş",
    "exportar vídeo",
    "exportar video",
    "renderizar",
    "descargar vídeo",
    "combinar",
    "regenerar",
    "subtítulos",
    "doblaje",
    "idioma",
    "historial",
  ],
  content: {
    en: {
      feature: "Publishing",
      title: "Video Export",
      sections: [
        {
          id: "overview",
          title: "What Video Export does",
          videoUrl: null,
          text:
            "Open **Video Export** from the **⋮** (kebab) menu, below **Export...** and **Subtitles** — it renders your training's scenes into an MP4 file exactly as they play in the learner view.\n\n" +
            "- Every scene's animations, narration, and timing are baked into the video frame by frame — no separate screen recording needed.\n" +
            "- Each scene renders as its own clip first; you review the clips, re-render any you don't like, then merge them into one final video yourself.\n" +
            "- This is a separate flow from the regular **Export...** menu item (SCORM/URL packaging) — that has its own guide.",
        },
        {
          id: "before",
          title: "Save first",
          videoUrl: null,
          text:
            "The export renders the **last saved** version of your training, not what's currently on your screen — unsaved edits to scenes, narration, animations, or text won't appear in the video. Save before you start; the start window reminds you of this too.\n\n" +
            "- If the training has no saved version yet (or the saved version has no scenes), the window opens on **You need to save first** instead of the export tools.\n" +
            "- Click **Save Now** to save without leaving the window, or save from the main toolbar and come back.\n" +
            "- Use **Check Again** to re-run the check — for example, right after saving from somewhere else.",
        },
        {
          id: "run",
          title: "Render and download",
          videoUrl: null,
          text:
            "Every scene is listed as a tile on the scene strip, with a large preview area beside it. Click **Start** in the top right; the **Start Video Production** window opens — click **Start** again to begin.\n\n" +
            "- If the training has subtitles, the start window also offers **Subtitle language (optional)** to add one language's subtitles to the video; leave it on **— No subtitles —** to skip them.\n" +
            "- Each tile shows its status and render progress. Rendering runs on a background queue — you can close the window, keep editing, or even reload the page; reopening Video Export picks the job back up.\n" +
            "- Rendering can take several minutes, depending on how many scenes you have and how long they are.\n" +
            "- Click a tile to watch that scene's clip in the large preview. Use its **Regenerate** icon to re-render just that scene — the current clip stays until the new one is ready — or its cancel icon to stop a queued or rendering scene.\n" +
            "- To leave a scene out, click the eye icon on its tile: before it's rendered it's marked **Won't Generate**, afterwards **Won't Merge**. Click again to include it.\n" +
            "- Drag finished tiles to change the order the scenes appear in the final video.\n" +
            "- Once every included scene is ready, click **Merge**. The merged video appears at the bottom of the strip; after changing scenes, run it again with **Re-merge**.\n" +
            "- Scene tiles and the merged video each have a download icon; the merged file is named after your training.",
        },
        {
          id: "manage-job",
          title: "Update, stop or start over",
          videoUrl: null,
          text:
            "Once a job has started, the toolbar at the top of the window manages it.\n\n" +
            "- **Update Scenes** — pulls the training's current scene list and queues any scene that hasn't rendered yet or failed. Finished clips are left untouched, so use **Regenerate** on a tile to refresh a scene you edited.\n" +
            "- **Stop All** — only shown while something is running; cancels the render or merge in progress. Finished clips are kept.\n" +
            "- **Start Over** — permanently deletes all export records for this training and returns to the start.\n" +
            "- The clock shows the total length of the video.\n" +
            "- **History** — lists previously merged videos, newest first; pick one to watch it in the preview or download it.",
        },
        {
          id: "languages",
          title: "Dub the video into other languages",
          videoUrl: null,
          text:
            "After merging, you can re-voice the finished video in another language without re-rendering the scenes — only the audio changes. Click **Languages** in the toolbar; the button only appears when voices for other languages are available.\n\n" +
            "- The window lists every ready video — the original and each dubbed language — with a button to watch it and a download icon.\n" +
            "- Below the list, click a language to dub the video into it, then confirm with **Generate**. Dubbing takes a few minutes depending on the video's length, and it spends credits.\n" +
            "- A progress bar shows the running dub; when it finishes, the new video opens in the preview.\n" +
            "- A language that already exists is marked; choosing it again regenerates it.",
        },
      ],
    },
    tr: {
      feature: "Yayımlama",
      title: "Video Dışa Aktarma",
      sections: [
        {
          id: "overview",
          title: "Video Dışa Aktarma ne yapar",
          videoUrl: null,
          text:
            "**⋮** (kebap) menüsünden, **Yayımla...** ve **Altyazılar** seçeneklerinin altındaki **Video Dışa Aktarma**'yı açın — eğitiminizin sahnelerini, öğrenci görünümünde oynadığı haliyle bir MP4 dosyasına render eder.\n\n" +
            "- Her sahnenin animasyonu, anlatımı ve zamanlaması kare kare videoya işlenir — ayrı bir ekran kaydına gerek yoktur.\n" +
            "- Her sahne önce kendi klibi olarak render edilir; klipleri inceler, beğenmediklerinizi yeniden render eder, ardından hepsini kendiniz tek bir videoda birleştirirsiniz.\n" +
            "- Bu, normal **Yayımla...** menü öğesinden (SCORM/URL paketleme) farklı, ayrı bir akıştır — onun kendi rehberi vardır.",
        },
        {
          id: "before",
          title: "Önce kaydedin",
          videoUrl: null,
          text:
            "Dışa aktarma işlemi eğitiminizin **en son kaydedilen** halini render eder, o an ekranınızda olanı değil — sahne, anlatım, animasyon veya metinde kaydetmediğiniz değişiklikler videoda görünmez. Başlamadan önce kaydedin; başlatma penceresi de bunu hatırlatır.\n\n" +
            "- Eğitimin henüz kaydedilmiş bir hali yoksa (veya kaydedilen halde hiç sahne yoksa) pencere, dışa aktarma araçları yerine **Önce kaydetmeniz gerekiyor** uyarısıyla açılır.\n" +
            "- Pencereden çıkmadan kaydetmek için **Şimdi Kaydet**'e tıklayın; ya da ana araç çubuğundan kaydedip geri dönün.\n" +
            "- Kontrolü yeniden çalıştırmak için — örneğin başka bir yerden kaydettikten hemen sonra — **Yeniden Kontrol Et**'i kullanın.",
        },
        {
          id: "run",
          title: "Render edin ve indirin",
          videoUrl: null,
          text:
            "Her sahne, sahne şeridinde bir kutucuk olarak listelenir; yanında büyük bir önizleme alanı bulunur. Sağ üstteki **Başla**'ya tıklayın; **Video Üretimini Başlat** penceresi açılır — başlatmak için yeniden **Başla**'ya tıklayın.\n\n" +
            "- Eğitimin altyazıları varsa başlatma penceresi, bir dilin altyazılarını videoya eklemek için **Altyazı dili (opsiyonel)** seçeneğini de sunar; altyazı istemiyorsanız **— Altyazısız —** olarak bırakın.\n" +
            "- Her kutucuk kendi durumunu ve render ilerlemesini gösterir. Render arka planda bir kuyrukta çalışır — pencereyi kapatabilir, düzenlemeye devam edebilir, hatta sayfayı yenileyebilirsiniz; Video Dışa Aktarma'yı tekrar açtığınızda iş kaldığı yerden devam eder.\n" +
            "- Render, sahne sayısına ve uzunluğuna göre birkaç dakika sürebilir.\n" +
            "- Bir kutucuğa tıklayarak o sahnenin klibini büyük önizlemede izleyin. Yalnızca o sahneyi yeniden render etmek için **Yeniden üret** simgesini — yeni klip hazır olana kadar mevcut klip yerinde kalır — sırada bekleyen veya render edilen bir sahneyi durdurmak için iptal simgesini kullanın.\n" +
            "- Bir sahneyi dışarıda bırakmak için kutucuğundaki göz simgesine tıklayın: render edilmeden önce **Oluşturulmayacak**, sonrasında **Birleştirilmeyecek** olarak işaretlenir. Yeniden dahil etmek için tekrar tıklayın.\n" +
            "- Sahnelerin son videodaki sırasını değiştirmek için tamamlanmış kutucukları sürükleyin.\n" +
            "- Dahil edilen tüm sahneler hazır olduğunda **Birleştir**'e tıklayın. Birleştirilmiş video şeridin en altında görünür; sahneleri değiştirdikten sonra **Yeniden Birleştir** ile tekrar çalıştırın.\n" +
            "- Sahne kutucuklarının ve birleştirilmiş videonun her birinde bir indirme simgesi bulunur; birleştirilmiş dosya eğitiminizin adıyla kaydedilir.",
        },
        {
          id: "manage-job",
          title: "Güncelleme, durdurma veya baştan başlama",
          videoUrl: null,
          text:
            "Bir iş başladıktan sonra pencerenin üstündeki araç çubuğu onu yönetir.\n\n" +
            "- **Sahneleri Güncelle** — eğitimin güncel sahne listesini çeker ve henüz render edilmemiş veya başarısız olan sahneleri kuyruğa alır. Tamamlanmış kliplere dokunulmaz; düzenlediğiniz bir sahneyi yenilemek için kutucuktaki **Yeniden üret**'i kullanın.\n" +
            "- **Tümünü Durdur** — yalnızca bir işlem sürerken görünür; devam eden render veya birleştirmeyi iptal eder. Tamamlanmış klipler korunur.\n" +
            "- **Yeniden Başla** — bu eğitime ait tüm dışa aktarma kayıtlarını kalıcı olarak siler ve başa döner.\n" +
            "- Saat simgesi videonun toplam süresini gösterir.\n" +
            "- **Geçmiş** — daha önce birleştirilmiş videoları en yeniden eskiye listeler; önizlemede izlemek veya indirmek için birini seçin.",
        },
        {
          id: "languages",
          title: "Videoyu başka dillerde seslendirme",
          videoUrl: null,
          text:
            "Birleştirdikten sonra, sahneleri yeniden render etmeden bitmiş videoyu başka bir dilde yeniden seslendirebilirsiniz — yalnızca ses değişir. Araç çubuğundaki **Diller**'e tıklayın; bu düğme yalnızca başka diller için ses seçenekleri mevcutsa görünür.\n\n" +
            "- Pencere hazır olan her videoyu — orijinali ve seslendirilmiş her dili — izleme düğmesi ve indirme simgesiyle listeler.\n" +
            "- Listenin altında, videoyu seslendirmek istediğiniz dile tıklayın ve **Üret** ile onaylayın. Seslendirme, videonun uzunluğuna göre birkaç dakika sürer ve kredi harcar.\n" +
            "- Süren seslendirmenin ilerlemesi bir çubukla gösterilir; tamamlandığında yeni video önizlemede açılır.\n" +
            "- Zaten var olan bir dil işaretlidir; onu yeniden seçmek o dili yeniden üretir.",
        },
      ],
    },
    es: {
      feature: "Publicación",
      title: "Exportar video",
      sections: [
        {
          id: "overview",
          title: "Qué hace Exportar video",
          videoUrl: null,
          text:
            "Abre **Exportar video** desde el menú **⋮** (de tres puntos), debajo de **Exportar...** y **Subtítulos**: convierte las escenas de tu formación en un archivo MP4, exactamente como se reproducen en la vista del alumno.\n\n" +
            "- Las animaciones, la narración y los tiempos de cada escena se graban en el vídeo fotograma a fotograma; no hace falta una grabación de pantalla aparte.\n" +
            "- Cada escena se renderiza primero como su propio clip; revisas los clips, vuelves a renderizar los que no te gusten y después los combinas tú mismo en un único vídeo final.\n" +
            "- Es un flujo distinto del elemento de menú **Exportar...** habitual (paquetes SCORM/URL), que tiene su propia guía.",
        },
        {
          id: "before",
          title: "Guarda primero",
          videoUrl: null,
          text:
            "La exportación renderiza la **última versión guardada** de tu formación, no lo que tienes en pantalla en ese momento: los cambios sin guardar en escenas, narración, animaciones o texto no aparecerán en el vídeo. Guarda antes de empezar; la ventana de inicio también te lo recuerda.\n\n" +
            "- Si la formación aún no tiene ninguna versión guardada (o la versión guardada no tiene escenas), la ventana se abre en **Primero debes guardar** en lugar de mostrar las herramientas de exportación.\n" +
            "- Haz clic en **Guardar Ahora** para guardar sin salir de la ventana, o guarda desde la barra de herramientas principal y vuelve.\n" +
            "- Usa **Comprobar de Nuevo** para repetir la comprobación; por ejemplo, justo después de guardar desde otro sitio.",
        },
        {
          id: "run",
          title: "Renderizar y descargar",
          videoUrl: null,
          text:
            "Cada escena aparece como un mosaico en la tira de escenas, con un área de vista previa grande al lado. Haz clic en **Empezar** arriba a la derecha; se abre la ventana **Iniciar Generación de Video**: vuelve a hacer clic en **Empezar** para comenzar.\n\n" +
            "- Si la formación tiene subtítulos, la ventana de inicio también ofrece **Idioma de subtítulos (opcional)** para añadir al vídeo los subtítulos de un idioma; déjalo en **— Sin subtítulos —** para omitirlos.\n" +
            "- Cada mosaico muestra su estado y el progreso del renderizado. El renderizado se ejecuta en una cola en segundo plano: puedes cerrar la ventana, seguir editando o incluso recargar la página; al volver a abrir Exportar video, el trabajo continúa donde estaba.\n" +
            "- El renderizado puede tardar varios minutos, según cuántas escenas tengas y lo largas que sean.\n" +
            "- Haz clic en un mosaico para ver el clip de esa escena en la vista previa grande. Usa su icono **Regenerar** para volver a renderizar solo esa escena (el clip actual se mantiene hasta que el nuevo esté listo), o su icono de cancelar para detener una escena en cola o en renderizado.\n" +
            "- Para dejar fuera una escena, haz clic en el icono del ojo de su mosaico: antes de renderizarse se marca como **No se generará** y, después, como **No se combinará**. Vuelve a hacer clic para incluirla.\n" +
            "- Arrastra los mosaicos terminados para cambiar el orden en que aparecen las escenas en el vídeo final.\n" +
            "- Cuando todas las escenas incluidas estén listas, haz clic en **Combinar**. El vídeo combinado aparece al final de la tira; si cambias escenas, vuelve a ejecutarlo con **Volver a Combinar**.\n" +
            "- Los mosaicos de escena y el vídeo combinado tienen cada uno un icono de descarga; el archivo combinado lleva el nombre de tu formación.",
        },
        {
          id: "manage-job",
          title: "Actualizar, detener o volver a empezar",
          videoUrl: null,
          text:
            "Una vez iniciado un trabajo, la barra de herramientas de la parte superior de la ventana permite gestionarlo.\n\n" +
            "- **Actualizar Escenas**: obtiene la lista actual de escenas de la formación y pone en cola cualquier escena que aún no se haya renderizado o que haya fallado. Los clips terminados no se tocan, así que usa **Regenerar** en un mosaico para actualizar una escena que hayas editado.\n" +
            "- **Detener Todo**: solo se muestra mientras hay algo en marcha; cancela el renderizado o la combinación en curso. Los clips terminados se conservan.\n" +
            "- **Volver a Empezar**: elimina de forma permanente todos los registros de exportación de esta formación y vuelve al inicio.\n" +
            "- El reloj muestra la duración total del vídeo.\n" +
            "- **Historial**: enumera los vídeos combinados anteriormente, del más reciente al más antiguo; elige uno para verlo en la vista previa o descargarlo.",
        },
        {
          id: "languages",
          title: "Doblar el vídeo a otros idiomas",
          videoUrl: null,
          text:
            "Después de combinar, puedes volver a locutar el vídeo terminado en otro idioma sin volver a renderizar las escenas: solo cambia el audio. Haz clic en **Idiomas** en la barra de herramientas; el botón solo aparece cuando hay voces disponibles para otros idiomas.\n\n" +
            "- La ventana enumera todos los vídeos listos (el original y cada idioma doblado), con un botón para verlo y un icono de descarga.\n" +
            "- Debajo de la lista, haz clic en un idioma para doblar el vídeo a ese idioma y confirma con **Generar**. El doblaje tarda unos minutos según la duración del vídeo y consume créditos.\n" +
            "- Una barra de progreso muestra el doblaje en curso; cuando termina, el vídeo nuevo se abre en la vista previa.\n" +
            "- Un idioma que ya existe aparece marcado; si lo vuelves a elegir, se regenera.",
        },
      ],
    },
  },
};

export default videoExportGuide;
